import { useI18n } from '@/i18n'
import { ArrowUp, Check, FileText } from '@/shared/icons/phosphor'
import { type JSX, Show, createSignal } from 'solid-js'
import { Loader } from '../Loader'
import './FileField.css'

export type FileRejectReason = 'type' | 'size'

export interface FileRejection {
  file: File
  reason: FileRejectReason
}

export interface FileFieldRenderState {
  dragging: boolean
  busy: boolean
  disabled: boolean
  browse: () => void
}

export interface FileFieldProps {
  onSelect: (files: File[]) => void
  accept?: string
  multiple?: boolean
  maxSizeBytes?: number
  disabled?: boolean
  busy?: boolean
  onReject?: (rejections: FileRejection[]) => void
  label: string
  interactive?: boolean
  class?: string
  variant?: 'dropzone' | 'compact'
  description?: string
  hint?: string
  actionLabel?: string
  busyLabel?: string
  fileName?: string
  error?: string
  children?: (state: FileFieldRenderState) => JSX.Element
}

export function fileMatchesAccept(file: File, accept: string | undefined): boolean {
  if (!accept?.trim()) {
    return true
  }
  const name = file.name.toLowerCase()
  const type = file.type.toLowerCase()
  return accept
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean)
    .some((token) => {
      if (token.startsWith('.')) {
        return name.endsWith(token)
      }
      if (token.endsWith('/*')) {
        return type.startsWith(token.slice(0, -1))
      }
      return type === token
    })
}

export function FileField(props: FileFieldProps) {
  const [dragDepth, setDragDepth] = createSignal(0)
  let inputRef: HTMLInputElement | undefined

  const busy = () => props.busy ?? false
  const interactive = () => props.interactive ?? true
  const disabled = () => props.disabled ?? false
  const inert = () => busy() || disabled()
  const dragging = () => dragDepth() > 0

  const browse = () => {
    if (!inert()) {
      inputRef?.click()
    }
  }

  const renderState: FileFieldRenderState = {
    get dragging() {
      return dragging()
    },
    get busy() {
      return busy()
    },
    get disabled() {
      return disabled()
    },
    browse,
  }

  const intake = (fileList: FileList | null | undefined) => {
    if (inert()) {
      return
    }
    const files = Array.from(fileList ?? [])
    if (files.length === 0) {
      return
    }
    const candidates = props.multiple ? files : files.slice(0, 1)
    const accepted: File[] = []
    const rejected: FileRejection[] = []
    for (const file of candidates) {
      if (!fileMatchesAccept(file, props.accept)) {
        rejected.push({ file, reason: 'type' })
      } else if (props.maxSizeBytes !== undefined && file.size > props.maxSizeBytes) {
        rejected.push({ file, reason: 'size' })
      } else {
        accepted.push(file)
      }
    }
    if (rejected.length > 0) {
      props.onReject?.(rejected)
    }
    if (accepted.length > 0) {
      props.onSelect(accepted)
    }
  }

  return (
    <div
      role={interactive() ? 'button' : undefined}
      tabIndex={interactive() ? (inert() ? -1 : 0) : undefined}
      aria-label={interactive() ? props.label : undefined}
      aria-disabled={interactive() ? inert() : undefined}
      aria-busy={busy()}
      data-dragging={dragging() ? 'true' : 'false'}
      data-disabled={disabled() ? 'true' : 'false'}
      data-invalid={props.error ? 'true' : 'false'}
      data-selected={props.fileName ? 'true' : 'false'}
      data-busy={busy() ? 'true' : 'false'}
      class={`${props.children ? '' : `ds-file-field ds-file-field--${props.variant ?? 'dropzone'}`} ${interactive() ? (inert() ? 'pointer-events-none' : 'cursor-pointer') : ''} focus-visible:outline-none [&:focus-visible>*]:outline-2 [&:focus-visible>*]:-outline-offset-2 [&:focus-visible>*]:outline-[var(--color-primary-500)] ${props.class ?? ''}`}
      onClick={interactive() ? browse : undefined}
      onKeyDown={(e) => {
        if (interactive() && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          browse()
        }
      }}
      onDragEnter={(e) => {
        e.preventDefault()
        if (!inert()) {
          setDragDepth((d) => d + 1)
        }
      }}
      onDragOver={(e) => {
        e.preventDefault()
      }}
      onDragLeave={(e) => {
        e.preventDefault()
        setDragDepth((d) => Math.max(0, d - 1))
      }}
      onDrop={(e) => {
        e.preventDefault()
        setDragDepth(0)
        if (!inert()) {
          intake(e.dataTransfer?.files)
        }
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={props.accept}
        multiple={props.multiple}
        disabled={inert()}
        tabIndex={-1}
        aria-hidden="true"
        class="hidden"
        onChange={(e) => {
          const input = e.currentTarget
          intake(input.files)
          input.value = ''
        }}
      />
      <Show when={!props.children} fallback={props.children?.(renderState)}>
        <FileFieldVisual {...props} state={renderState} />
      </Show>
    </div>
  )
}

function FileFieldVisual(props: FileFieldProps & { state: FileFieldRenderState }) {
  const { t } = useI18n()
  const state = props.state
  return (
    <>
      <div class="ds-file-field-art" aria-hidden="true">
        <span class="ds-file-field-sheet ds-file-field-sheet--back" />
        <span class="ds-file-field-sheet ds-file-field-sheet--front">
          <FileText class="ds-file-field-document" />
        </span>
        <span class="ds-file-field-symbol rounded-full">
          <Show when={!state.busy} fallback={<Loader size={20} tone="current" />}>
            <Show when={props.fileName} fallback={<ArrowUp class="h-4 w-4" weight="bold" />}>
              <Check class="h-4 w-4" weight="bold" />
            </Show>
          </Show>
        </span>
      </div>
      <div class="ds-file-field-content">
        <span class="ds-file-field-title">
          {state.busy
            ? (props.busyLabel ?? t('fileField.processing'))
            : state.dragging
              ? t('fileField.release')
              : props.fileName || props.label}
        </span>
        <Show when={props.description}>
          <span class="ds-file-field-description">{props.description}</span>
        </Show>
        <Show when={props.hint}>
          <span class="ds-file-field-hint">{props.hint}</span>
        </Show>
      </div>
      <Show when={!state.busy && (props.interactive ?? true)}>
        <span class="ds-file-field-action">
          {props.actionLabel ?? (props.fileName ? t('fileField.replace') : t('fileField.browse'))}
        </span>
      </Show>
      <Show when={props.error}>
        <span class="ds-file-field-error" role="alert">
          {props.error}
        </span>
      </Show>
    </>
  )
}
